import type { VedurWeatherResponse, WeatherData, WeatherForecast } from '@/types';
import { weatherApi } from '@/lib/api';
import { VEDUR_WEATHER_MAP } from '@/constants/api';
import { useOptimizedQuery } from './useOptimizedQuery';

interface ProcessedWeatherData extends WeatherData {
  forecast: WeatherForecast[];
}

/**
 * Get condition type from Vedur.is weather description
 */
export function getWeatherCondition(description: string): string {
  return VEDUR_WEATHER_MAP[description] || 'cloudy';
}

/**
 * Transform Vedur.is API response to app format
 * Groups hourly forecasts by date to get daily min/max temperatures
 */
function transformWeatherData(data: VedurWeatherResponse): ProcessedWeatherData {
  const forecasts = data.forecasts;

  if (forecasts.length === 0) {
    throw new Error('No forecast data available');
  }

  // Current weather is the first forecast entry
  const current = forecasts[0];

  // Group forecasts by date to calculate daily min/max
  const dailyMap = new Map<string, { temps: number[]; descriptions: string[] }>();

  forecasts.forEach((fc) => {
    const date = fc.ftime.split(' ')[0]; // "2025-12-05 03:00:00" -> "2025-12-05"
    if (!dailyMap.has(date)) {
      dailyMap.set(date, { temps: [], descriptions: [] });
    }
    const day = dailyMap.get(date)!;
    day.temps.push(fc.temperature);
    day.descriptions.push(fc.weatherDescription);
  });

  // Convert to daily forecast array (skip first day as it's "current")
  const dailyForecasts: WeatherForecast[] = [];
  let isFirst = true;

  dailyMap.forEach((dayData, date) => {
    if (isFirst) {
      isFirst = false;
      return; // Skip current day
    }
    // Most common description for the day (pick midday one or first)
    const midDescription = dayData.descriptions[Math.floor(dayData.descriptions.length / 2)] || dayData.descriptions[0];

    dailyForecasts.push({
      date,
      temperatureMax: Math.max(...dayData.temps),
      temperatureMin: Math.min(...dayData.temps),
      condition: getWeatherCondition(midDescription),
      weatherDescription: midDescription,
    });
  });

  return {
    temperature: current.temperature,
    condition: getWeatherCondition(current.weatherDescription),
    weatherDescription: current.weatherDescription,
    windSpeed: current.windSpeed,
    windDirection: current.windDirection,
    lastUpdated: data.lastUpdated,
    forecast: dailyForecasts.slice(0, 5), // 5-day forecast
  };
}

/**
 * Hook to fetch current weather and forecast for Reykjavik
 */
export function useCurrentWeather() {
  return useOptimizedQuery({
    queryKey: ['weather', 'current'],
    queryFn: async () => {
      const data = await weatherApi.getCurrent();
      return transformWeatherData(data);
    },
    tier: 'dynamic', // 1-hour cache
  });
}
